// Portfolio site and investigator data previously loaded from deleted JSON files.
// Will be wired to real API data in a future iteration.

interface SiteProfile {
  survey_status?: string;
  staff_stability?: string;
  competing_trial_load?: unknown;
  therapeutic_areas?: string[];
  dqs?: {
    recruitment_status: string;
    historical: {
      screened_per_month: number;
      screen_fail_rate_pct: number;
      days_to_activation: number;
      days_to_first_patient: number;
      studies_count: number;
    };
    competing_sponsor_trials: { sponsor: string; phase: string }[];
  };
}

interface Investigator {
  name: string;
  inv_key: string;
  specialty: string;
  years_experience: number;
  dqs?: {
    fda_debarment: { status: string; checked_date: string };
    total_studies_disclosed: number;
    historical: {
      screened_per_month: number;
      screen_fail_rate_pct: number;
      days_to_activation: number;
    };
  };
}

interface PlannedSiteExtended {
  site_key: string;
  site_name: string;
  city: string;
  country: string;
  status: string;
  notes?: string;
  activationOffsetWeeks?: number;
  screenedPerMonth?: number;
  screenFailureRate?: number;
  patientPoolCapacity?: number;
  siteStartup?: {
    sivDate?: string;
    irbApproved?: string;
    [key: string]: unknown;
  };
}

const SITE_MAP = new Map<string, SiteProfile>();
const INV_BY_SITE = new Map<string, Investigator[]>();

function fmt(v: unknown): unknown {
  return v !== undefined && v !== null && v !== "" ? v : null;
}

function siteLine(site: PlannedSiteExtended): string {
  const parts = [
    `${site.site_name}, ${site.city}, ${site.country} (${site.site_key})`,
    `plan_status=${site.status}`,
  ];
  if (fmt(site.activationOffsetWeeks))
    parts.push(`activation_offset=${site.activationOffsetWeeks}wk`);
  if (fmt(site.screenedPerMonth))
    parts.push(`plan_screened_per_mo=${site.screenedPerMonth}`);
  if (fmt(site.screenFailureRate))
    parts.push(`plan_screen_fail=${site.screenFailureRate}%`);
  if (fmt(site.patientPoolCapacity))
    parts.push(`pool_capacity=${site.patientPoolCapacity}`);
  if (site.siteStartup?.sivDate) parts.push(`siv=${site.siteStartup.sivDate}`);
  if (site.siteStartup?.irbApproved)
    parts.push(`irb_approved=${site.siteStartup.irbApproved}`);
  if (site.notes) parts.push(`notes="${site.notes}"`);

  const profile = SITE_MAP.get(site.site_key);
  if (profile) {
    if (profile.survey_status) parts.push(`survey=${profile.survey_status}`);
    if (profile.staff_stability) parts.push(`staff=${profile.staff_stability}`);
    if (fmt(profile.competing_trial_load))
      parts.push(`competing_load=${profile.competing_trial_load}`);
    if (profile.therapeutic_areas?.length)
      parts.push(`TAs=[${profile.therapeutic_areas.join(", ")}]`);
    const d = profile.dqs;
    if (d) {
      const sponsors = d.competing_sponsor_trials
        .map((t) => `${t.sponsor}/${t.phase}`)
        .join(", ");
      parts.push(
        `DQS_status=${d.recruitment_status}`,
        `DQS_screened_per_mo=${d.historical.screened_per_month}`,
        `DQS_screen_fail=${d.historical.screen_fail_rate_pct}%`,
        `DQS_days_to_activate=${d.historical.days_to_activation}`,
        `DQS_days_to_1st_patient=${d.historical.days_to_first_patient}`,
        `DQS_studies=${d.historical.studies_count}`,
        sponsors ? `DQS_competing=[${sponsors}]` : "DQS_competing=[]",
      );
    }
  }
  return "  " + parts.join(", ");
}

function invLine(inv: Investigator): string {
  const parts = [
    `${inv.name} (${inv.inv_key})`,
    `specialty=${inv.specialty}`,
    `experience=${inv.years_experience}yr`,
  ];
  const d = inv.dqs;
  if (d) {
    parts.push(
      `DQS_fda=${d.fda_debarment.status}(${d.fda_debarment.checked_date})`,
      `DQS_studies=${d.total_studies_disclosed}`,
      `DQS_screened_per_mo=${d.historical.screened_per_month}`,
      `DQS_screen_fail=${d.historical.screen_fail_rate_pct}%`,
      `DQS_days_to_activate=${d.historical.days_to_activation}`,
    );
  }
  return "    " + parts.join(", ");
}

/**
 * Build a compact plan context string for Ask Vivo.
 * @param plan
 * @param tab  - current route tab (home | sites | milestones | enrollment | vendors | budget)
 */
export function buildPlanContext(
  plan: import("../views/TrialPlanning/types").TrialPlan | null | undefined,
  tab: string,
): string {
  if (!plan) return "";
  const lines: string[] = [];

  // ── Plan header (always) ─────────────────────────────────────────────────
  lines.push(`[Current Trial Plan: "${plan.name}"]`);
  lines.push(
    `[Site status legend: plan_status=selected means the site has been confirmed for this trial; ` +
      `plan_status=candidate means the site is under consideration but not yet committed; ` +
      `plan_status=rejected means the site has been explicitly excluded. ` +
      `Sites not listed here are in the portfolio but have not been added to this plan.]`,
  );
  const meta = [`Phase: ${plan.phase}`, `Indication: ${plan.indication}`];
  if (fmt(plan.targetEnrollment))
    meta.push(`Target enrollment: ${plan.targetEnrollment} patients`);
  if (fmt(plan.screenFailureRate))
    meta.push(`Assumed screen failure rate: ${plan.screenFailureRate}%`);
  if (fmt(plan.rampUpMonths)) meta.push(`Ramp-up: ${plan.rampUpMonths} months`);
  lines.push(meta.join(" | "));
  if (plan.objective) lines.push(`Objective: ${plan.objective}`);
  if (plan.primaryEndpoint)
    lines.push(`Primary endpoint: ${plan.primaryEndpoint}`);

  // ── Protocol detail (home tab) ───────────────────────────────────────────
  if (tab === "home") {
    if (plan.inclusionCriteria)
      lines.push(`Inclusion criteria: ${plan.inclusionCriteria}`);
    if (plan.exclusionCriteria)
      lines.push(`Exclusion criteria: ${plan.exclusionCriteria}`);
    if (plan.overview) lines.push(`Overview: ${plan.overview}`);
  }

  // ── Planned sites (all tabs — core feasibility reference) ───────────────
  if (plan.sites?.length) {
    const nSelected = plan.sites.filter((s) => s.status === "selected").length;
    const nCandidate = plan.sites.filter(
      (s) => s.status === "candidate",
    ).length;
    const nRejected = plan.sites.filter((s) => s.status === "rejected").length;
    lines.push(
      `\nShortlisted Sites (${plan.sites.length} total — ${nSelected} selected, ${nCandidate} candidate, ${nRejected} rejected):`,
    );
    lines.push(
      `  Each site below was manually added to this plan from the portfolio. ` +
        `status=selected: confirmed for the trial. status=candidate: under consideration. ` +
        `status=rejected: explicitly excluded. The notes field captures the reason or any site-specific context.`,
    );
    for (const site of plan.sites) {
      lines.push(siteLine(site as PlannedSiteExtended));
      // Investigators at this site (with DQS debarment data)
      const invs = INV_BY_SITE.get(site.site_key) ?? [];
      for (const inv of invs) lines.push(invLine(inv));
    }
  } else {
    lines.push("\nPlanned Sites: none added yet");
  }

  // ── Milestones (milestones + home tabs) ──────────────────────────────────
  if ((tab === "milestones" || tab === "home") && plan.milestones?.length) {
    lines.push(`\nMilestones (${plan.milestones.length}):`);
    for (const m of plan.milestones) {
      const range =
        m.end_date && m.end_date !== m.start_date
          ? `${m.start_date} – ${m.end_date}`
          : m.start_date;
      lines.push(`  ${m.event_name} (${m.event_type}): ${range}`);
    }
  }

  // ── Enrollment scenarios (enrollment + home tabs) ────────────────────────
  if (
    (tab === "enrollment" || tab === "home") &&
    plan.enrollmentScenarios?.length
  ) {
    lines.push(`\nEnrollment Scenarios:`);
    for (const s of plan.enrollmentScenarios) {
      lines.push(
        `  ${s.name}: rate ${s.rateMultiplier}x, activation lag ${s.activationLagMultiplier}x`,
      );
    }
  }

  // ── Vendors (vendors tab) ────────────────────────────────────────────────
  if (tab === "vendors" && plan.vendors?.length) {
    lines.push(`\nVendors (${plan.vendors.length}):`);
    for (const v of plan.vendors) {
      lines.push(`  ${v.name} (${v.type}, ${v.status}): ${v.services}`);
    }
  }

  // ── Budget (budget tab) ──────────────────────────────────────────────────
  if (tab === "budget" && plan.budget?.length) {
    const total = plan.budget.reduce(
      (sum, b) => sum + b.quantity * b.unitCost,
      0,
    );
    lines.push(
      `\nBudget Lines (${plan.budget.length}, total ~$${total.toLocaleString()}):`,
    );
    for (const b of plan.budget) {
      const lineTotal = b.quantity * b.unitCost;
      lines.push(
        `  [${b.category}] ${b.lineItem}: ${b.quantity} × $${b.unitCost} = $${lineTotal.toLocaleString()}` +
          (b.notes ? ` — ${b.notes}` : ""),
      );
    }
  }

  return lines.join("\n");
}
